'use client'
import { useRef, useState } from 'react'
import { cellState, dayDisabled, dayState, type MonthGridModel, type Selected } from './_lib/bookingCalendarModel'

type Props = {
  model: MonthGridModel
  qty: number
  selected: Selected
  hover: string | null
  /** Inclusive 'YYYY-MM-DD' floor — days before this are past/non-bookable. */
  minDate: string
  onPick: (iso: string) => void
  onHover: (iso: string | null) => void
  loading?: boolean
  /** false when the CalendarHeader's dropdowns already name this month. */
  showLabel?: boolean
}

function fmtLong(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', timeZone: 'UTC',
  })
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** A cell of the month grid: either a leading blank or a real day. `index` is
 *  the day's position in `model.cells`, which is what the roving tabindex and
 *  `cellRefs` are keyed on. */
type DaySlot =
  | { kind: 'blank'; key: string }
  | { kind: 'day'; key: string; cell: MonthGridModel['cells'][number]; index: number }

/**
 * One month of the booking calendar. Pure-presentational over
 * bookingCalendarModel — the month math and range/preview state come from the
 * model so two of these side-by-side render a single coherent range. Keeps the
 * original calendar's a11y: role=grid, 44px targets, aria labels, roving
 * tabindex with arrow-key navigation within the month.
 *
 * Cell color coding — all via the SELECTION family, never the CTA. That token
 * is fed from the brand accent and is the color of Reserve; a calendar painted
 * with it leaves the money button no more weight than a Tuesday.
 *
 *   available  → --color-selected-soft, deepening to -mid on hover
 *   soldout    → flat muted fill (no strikethrough — the fill carries it)
 *   past       → no fill, faded numeral
 *   selected   → solid --color-selected-STRONG with its own label token. Not
 *                --color-selected: a dealer may point their selection identity
 *                at the secondary (Adventure Elevated does), and this one cell
 *                has to out-rank a whole grid already painted in it. The strong
 *                token reads the other brand slot for exactly that reason, and
 *                collapses onto --color-selected for every tenant who has not
 *                opted in.
 *   range span → --color-selected-mid, the same step as hover. The old scheme
 *                gave preview and range their own tints 5% apart, which is
 *                below the threshold of noticing and cost a token for nothing;
 *                the two states also barely co-occur (preview shows only while
 *                picking a range end). A sold-out day interior to a span keeps
 *                its sold-out fill — the span tint skips it.
 *
 * No Tailwind alpha suffixes anywhere: `/25` composites the token back toward
 * the canvas and discards the contrast guard the derivation just applied.
 */
export function MonthGrid({ model, qty, selected, hover, minDate, onPick, onHover, loading, showLabel = true }: Props) {
  const [focusIdx, setFocusIdx] = useState(0)
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([])
  const { label, leadingBlanks, cells } = model

  // Chunked into weeks so each one can carry `role="row"` (see the grid below).
  // Day slots keep their index into `cells` — `cellRefs` and the roving
  // tabindex are addressed by that index, NOT by position in the week, so
  // arrow-key navigation is unaffected by the grouping.
  const slots: DaySlot[] = [
    ...Array.from({ length: leadingBlanks }, (_, i) => ({ kind: 'blank' as const, key: `b${i}` })),
    ...cells.map((cell, index) => ({ kind: 'day' as const, key: cell.iso, cell, index })),
  ]
  const weeks: DaySlot[][] = []
  for (let i = 0; i < slots.length; i += 7) weeks.push(slots.slice(i, i + 7))

  const onKeyDown = (e: React.KeyboardEvent, iso: string, idx: number) => {
    let next = idx
    if (e.key === 'ArrowRight') next = idx + 1
    else if (e.key === 'ArrowLeft') next = idx - 1
    else if (e.key === 'ArrowDown') next = idx + 7
    else if (e.key === 'ArrowUp') next = idx - 7
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(iso); return }
    else return
    e.preventDefault()
    if (next >= 0 && next < cells.length) { setFocusIdx(next); cellRefs.current[next]?.focus() }
  }

  return (
    <div>
      {showLabel && (
        <p className="text-center font-medium text-[var(--color-text-primary)] mb-3">{label}</p>
      )}
      {loading ? (
        <div className="grid grid-cols-7 gap-1" aria-hidden="true">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-11 rounded bg-[var(--color-border)]/40 animate-pulse" />
          ))}
        </div>
      ) : (
        // ARIA `grid` must contain `row`s, and `columnheader` / `gridcell` must
        // be contained BY a row — flat children fail both rules (caught by
        // Lighthouse on shop.adventureelevated.com, 2026-07-31). The rows use
        // `display: contents` so the single 7-column CSS grid is unchanged
        // visually while the accessibility tree gets the structure it requires.
        // Roles on a `display: contents` element are exposed correctly by every
        // engine we target; the old bug that dropped them was fixed in Chrome
        // 89 / Firefox 82 / Safari 15.4.
        <div role="grid" aria-label={`${label} availability`} className="grid grid-cols-7 gap-1">
          <div role="row" className="contents">
            {WEEKDAYS.map(d => (
              <div key={d} role="columnheader" className="text-center text-xs text-[var(--color-text-muted)] py-1">{d}</div>
            ))}
          </div>
          {weeks.map((week, w) => (
            <div key={`w${w}`} role="row" className="contents">
              {week.map(slot => {
                if (slot.kind === 'blank') return <div key={slot.key} role="gridcell" />
                const { cell: c, index: i } = slot
                const ds = dayState(c.iso, c.remaining, qty, minDate)
                const unavailable = dayDisabled(c.iso, c.remaining, qty, minDate)
                const st = cellState(c.iso, selected, hover)
                const isEndpoint = st.isStart || st.isEnd
                const aria = unavailable ? `${fmtLong(c.iso)}, unavailable` : `${fmtLong(c.iso)}, ${c.remaining} available`
                return (
                  <button
                    key={c.iso}
                    ref={el => { cellRefs.current[i] = el }}
                    role="gridcell"
                    type="button"
                    tabIndex={i === focusIdx ? 0 : -1}
                    aria-label={aria}
                    aria-disabled={unavailable}
                    disabled={unavailable}
                    onKeyDown={e => onKeyDown(e, c.iso, i)}
                    onClick={() => !unavailable && onPick(c.iso)}
                    onMouseEnter={() => !unavailable && onHover(c.iso)}
                    className={[
                      'min-h-[44px] rounded text-sm transition-colors',
                      ds === 'past' ? 'text-[var(--color-text-muted)] opacity-40 cursor-not-allowed' : '',
                      ds === 'soldout' ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] cursor-not-allowed' : '',
                      isEndpoint
                        ? 'bg-[var(--color-selected-strong)] text-[var(--color-selected-strong-text)]'
                        : '',
                      ds === 'available' && !isEndpoint && (st.inRange || st.isPreview)
                        ? 'bg-[var(--color-selected-mid)] text-[var(--color-text-primary)]'
                        : '',
                      ds === 'available' && !isEndpoint && !st.inRange && !st.isPreview
                        ? 'bg-[var(--color-selected-soft)] text-[var(--color-text-primary)] hover:bg-[var(--color-selected-mid)]'
                        : '',
                    ].join(' ')}
                  >{c.dayNum}</button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
